import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MergeQueueData, MergeQueueEntry, MQFailure, MQFailureCore, PullRequest } from './types';
import { useAccessToken } from '@app/auth';
import { fetchApi } from '@app/hooks/api';
import { MergedPRs } from './MergedPRs';
import { QueueEvictions } from './QueueEvictions';
import { PipelineModal } from './PipelineModal';
import { REPO_CONFIG, formatDate, formatDuration, formatFullDateTime } from './utils';
import { failureReasonContent, failureStageContent } from './failureBadges';
import './MergeQueue.css';

type TabType = 'overview' | 'merged' | 'evictions';

const RECENT_FAILURES_LIMIT = 3;

// Per-PR state for the expensive half of the failure card (Failure Stage/Failure
// Reason badges), resolved in the background after the core preview renders.
// 'excluded' means every one of the PR's failed builds turned out to be merge-queue-
// eviction noise — the card is dropped rather than shown with nothing legitimate.
type FailureDetailState =
  | { status: 'loading' }
  | { status: 'ready'; data: MQFailure }
  | { status: 'excluded' }
  | { status: 'error' };

const STATUS_COLORS: Record<string, { bg: string; border: string }> = {
  mergeable:       { bg: '#28a745', border: '#1e7e34' },
  unmergeable:     { bg: '#dc3545', border: '#bd2130' },
  awaiting_checks: { bg: '#d97706', border: '#b45309' },
  queued:          { bg: '#0056b3', border: '#003d82' },
};

export const MergeQueue: React.FC = () => {
  const [queueData, setQueueData] = useState<MergeQueueData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [pipelinePr, setPipelinePr] = useState<{ number: number; title: string } | null>(null);
  const [recentFailures, setRecentFailures] = useState<MQFailureCore[]>([]);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [failureDetails, setFailureDetails] = useState<Record<number, FailureDetailState>>({});
  const getToken = useAccessToken();

  const fetchMergeQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchApi<MergeQueueData>(
        `/api/mergequeue?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}`,
        getToken
      );
      setQueueData(data);
    } catch (err) {
      console.error('[MergeQueue] Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setQueueData(null);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchMergeQueue();
  }, [fetchMergeQueue]);

  // Persist the merge-queue lifecycle into the mergequeue_event log. Fire-and-forget
  // by design: this page renders live Jenkins/GitHub data and must not wait on — or
  // fail because of — the historical write behind it, so nothing here is awaited and
  // no render depends on it.
  //
  // Firing on every mount is intentional and cheap. How often a sync actually runs is
  // the server's decision (it holds a cooldown window); a call inside that window
  // returns immediately having touched neither Jenkins nor the database. Rate limiting
  // cannot live here — several engineers with several tabs each have their own client.
  //
  // Guarded to once per mount rather than keyed on getToken: useAccessToken rebuilds
  // getToken whenever MSAL's `accounts` array changes identity, which happens on
  // re-render, so a plain [getToken] dependency would POST again on every render —
  // and each POST costs a Jenkins round trip. The other fetches on this page have the
  // same dependency, but they are reads; this one writes, so it takes the guard.
  const syncFiredRef = useRef(false);
  useEffect(() => {
    if (syncFiredRef.current) return;
    syncFiredRef.current = true;

    fetchApi(
      `/api/mergequeue/events/sync?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}`,
      getToken,
      { method: 'POST' }
    ).catch(err => {
      console.warn('[MergeQueue] Background event sync failed (metrics history only):', err);
    });
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    const fetchFailures = async () => {
      setFailuresLoading(true);
      setFailureDetails({});
      try {
        // Fast preview first — no wfapi/consoleText resolution, so the card grid
        // renders immediately instead of blocking on every failed build in the repo's
        // history just to show the 3 most recent ones.
        const core = await fetchApi<MQFailureCore[]>(
          `/api/mergequeue/recent-failures/core?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}&limit=${RECENT_FAILURES_LIMIT}`,
          getToken
        );
        if (cancelled) return;
        setRecentFailures(core);

        // Resolve each shown PR's real failure detail (Failure Stage/Reason badges)
        // independently in the background — one card's slow consoleText fetch never
        // blocks another's badges from appearing.
        core.forEach(f => {
          setFailureDetails(prev => ({ ...prev, [f.prNumber]: { status: 'loading' } }));
          fetchApi<MQFailure | null>(
            `/api/mergequeue/recent-failures/detail?organization=${REPO_CONFIG.organization}&repo=${REPO_CONFIG.repo}&prNumber=${f.prNumber}`,
            getToken
          ).then(detail => {
            if (cancelled) return;
            setFailureDetails(prev => ({
              ...prev,
              [f.prNumber]: detail ? { status: 'ready', data: detail } : { status: 'excluded' },
            }));
          }).catch(err => {
            console.error(`[MergeQueue] Error resolving failure detail for PR #${f.prNumber}:`, err);
            if (!cancelled) setFailureDetails(prev => ({ ...prev, [f.prNumber]: { status: 'error' } }));
          });
        });
      } catch (err) {
        console.error('[MergeQueue] Error fetching recent failures:', err);
      } finally {
        if (!cancelled) setFailuresLoading(false);
      }
    };
    fetchFailures();
    return () => { cancelled = true; };
  }, [getToken]);

  return (
    <div className="merge-queue-container">

      {/* Compact page header */}
      <div className="merge-queue-header">
        <h1>🔀 Merge Queue <span className="header-repo">· Teladoc / telapp</span></h1>
      </div>

      {/* Tab navigation */}
      <div className="merge-queue-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <span className="tab-icon">📊</span>
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'merged' ? 'active' : ''}`}
          onClick={() => setActiveTab('merged')}
        >
          <span className="tab-icon">✅</span>
          Merged PRs
        </button>
        <button
          className={`tab-button ${activeTab === 'evictions' ? 'active' : ''}`}
          onClick={() => setActiveTab('evictions')}
        >
          <span className="tab-icon">⚠️</span>
          Failed PRs
        </button>
      </div>

      {/* Tab content */}
      <div className="tab-content">

        {/* ── Overview ── */}
        <div className={`content-sections ${activeTab === 'overview' ? 'active' : 'hidden'}`}>

          {/* Queue widget — grows to fill available space */}
          <div className="widget-panel queue-widget">
            <div className="widget-header widget-header-queue">
              <span className="widget-title">Queue</span>
              {queueData && (
                <>
                  <span className="widget-count-badge">
                    {queueData.total_count} PR{queueData.total_count !== 1 ? 's' : ''}
                  </span>
                  <span className="widget-updated">Updated {formatDate(queueData.fetched_at)}</span>
                </>
              )}
            </div>
            <div className="queue-widget-body">
              {loading && !queueData ? (
                <div className="loading-container">
                  <div className="dx-spinner dx-spinner--lg" />
                  <p>Loading merge queue…</p>
                </div>
              ) : error ? (
                <div className="error-container">
                  <p className="error-message">❌ {error}</p>
                  {error.includes('rate limit') && (
                    <p className="error-hint">
                      GitHub API rate limit exceeded — please wait and retry.
                    </p>
                  )}
                  <button className="retry-button" onClick={() => fetchMergeQueue()} disabled={loading}>
                    {loading ? 'Retrying…' : 'Retry'}
                  </button>
                </div>
              ) : (
                <table className="queue-table">
                  {/* colgroup drives all widths; PR col has no width so it gets remaining space */}
                  <colgroup>
                    <col style={{ width: '44px' }} />
                    <col />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="col-position">#</th>
                      <th className="col-pr">Pull Request</th>
                      <th className="col-chg-ticket">CHG Ticket</th>
                      <th className="col-author">Author</th>
                      <th className="col-status">Status</th>
                      <th className="col-enqueue-time">Enqueued On</th>
                      <th className="col-est-merge-time">Est. Merge In</th>
                    </tr>
                  </thead>
                  <tbody>
                    {queueData && queueData.queue.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="empty-queue-row">
                          <div className="empty-queue-message">
                            <span className="empty-icon">🎉</span> No PRs in the merge queue — All clear!
                          </div>
                        </td>
                      </tr>
                    ) : (
                      queueData?.queue.map((entry: MergeQueueEntry, index: number) => (
                        <tr key={entry.pr.number} className={`queue-row ${index === 0 ? 'first-in-queue' : ''}`}>
                          <td className="col-position">
                            <div className="position-badge">{index + 1}</div>
                          </td>
                          <td className="col-pr">
                            <a href={entry.pr.url} target="_blank" rel="noopener noreferrer" className="pr-link">
                              {entry.pr.title} <span className="pr-number-muted">(#{entry.pr.number})</span>
                            </a>
                          </td>
                          <td className="col-chg-ticket">
                            {entry.pr.chg_ticket ? (
                              <a
                                href={`https://teladochealth.atlassian.net/browse/${entry.pr.chg_ticket}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {entry.pr.chg_ticket}
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="col-author">
                            <div className="author-cell">
                              <img src={entry.pr.author_avatar} alt={entry.pr.author} className="author-avatar" />
                              <span className="author-name">{entry.pr.author}</span>
                            </div>
                          </td>
                          <td className="col-status">
                            {index === 0 && entry.status === 'awaiting_checks' ? (
                              <button
                                className="status-badge status-badge-clickable"
                                style={{
                                  backgroundColor: STATUS_COLORS[entry.status]?.bg ?? '#6c757d',
                                  borderColor: STATUS_COLORS[entry.status]?.border ?? '#495057',
                                }}
                                onClick={() => setPipelinePr({ number: entry.pr.number, title: entry.pr.title })}
                                title="Click to view pipeline stages"
                              >
                                <span className="status-spinner" />
                                Processing
                              </button>
                            ) : (
                              <span
                                className="status-badge"
                                style={{
                                  backgroundColor: STATUS_COLORS[entry.status]?.bg ?? '#6c757d',
                                  borderColor: STATUS_COLORS[entry.status]?.border ?? '#495057',
                                }}
                              >
                                {entry.status.replace(/_/g, ' ')}
                              </span>
                            )}
                          </td>
                          <td className="col-enqueue-time">
                            {entry.enqueued_at
                              ? (
                                <span
                                  className="timestamp-rel"
                                  data-full={formatFullDateTime(entry.enqueued_at)}
                                  onMouseMove={e => {
                                    e.currentTarget.style.setProperty('--tip-x', `${e.clientX + 12}px`);
                                    e.currentTarget.style.setProperty('--tip-y', `${e.clientY + 16}px`);
                                  }}
                                >
                                  {formatDate(entry.enqueued_at)}
                                </span>
                              )
                              : <span className="text-muted">—</span>}
                          </td>
                          <td className="col-est-merge-time">
                            {entry.estimated_time
                              ? <span className="est-merge-time">~{entry.estimated_time}</span>
                              : <span className="text-muted">—</span>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>

        {/* ── Merged PRs tab ── */}
        <div className={activeTab === 'merged' ? 'active' : 'hidden'}>
          <MergedPRs />
        </div>

        {/* ── Failed PRs tab ── */}
        <div className={activeTab === 'evictions' ? 'active' : 'hidden'}>
          <QueueEvictions />
        </div>

      </div>

      {/* Bottom widget row — grid row 4 (auto), always fits in viewport */}
      {activeTab === 'overview' && (
        <div className="overview-bottom-columns">

          {/* Recently Merged widget */}
          <div className="widget-panel">
            <div className="widget-header widget-header-success">
              <span className="widget-dot dot-success" />
              <span className="widget-title">Recently Merged</span>
            </div>
            <div className="widget-body">
              {loading && !queueData ? (
                <p className="widget-empty">Loading…</p>
              ) : (queueData?.recently_merged ?? []).slice(0, 3).length === 0 ? (
                <p className="widget-empty">No recently merged PRs.</p>
              ) : (
                (queueData?.recently_merged ?? []).slice(0, 3).map((pr: PullRequest) => (
                  <div key={pr.number} className="overview-card merged-card">
                    <div className="overview-card-header">
                      {pr.chg_ticket && (
                        <a
                          href={`https://teladochealth.atlassian.net/browse/${pr.chg_ticket}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="overview-card-chg"
                        >
                          {pr.chg_ticket}
                        </a>
                      )}
                      <a href={pr.url} target="_blank" rel="noopener noreferrer" className="overview-card-title">
                        {pr.title}
                      </a>
                      <span className="overview-card-pr-number">#{pr.number}</span>
                    </div>
                    <div className="overview-card-meta">
                      <div className="overview-card-author">
                        <img src={pr.author_avatar} alt={pr.author} className="overview-card-avatar" />
                        <span>{pr.merged_by ?? pr.author}</span>
                      </div>
                      {pr.mq_first_queued_at && pr.merged_at && (
                        <span className="overview-card-mq-stat">{formatDuration(pr.mq_first_queued_at, pr.merged_at)} in MQ</span>
                      )}
                      {pr.mq_attempts != null && (
                        <span className="overview-card-mq-stat">
                          {pr.mq_attempts} attempt{pr.mq_attempts !== 1 ? 's' : ''}
                        </span>
                      )}
                      {pr.merged_at && (
                        <span className="overview-card-time">{formatDate(pr.merged_at)}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent MQ Failures widget */}
          <div className="widget-panel">
            <div className="widget-header widget-header-danger">
              <span className="widget-dot dot-danger" />
              <span className="widget-title">Recent MQ Failures</span>
            </div>
            <div className="widget-body">
              {failuresLoading ? (
                <p className="widget-empty">Loading…</p>
              ) : recentFailures.filter(f => failureDetails[f.prNumber]?.status !== 'excluded').length === 0 ? (
                <p className="widget-empty">No recent MQ failures.</p>
              ) : (
                recentFailures
                  .filter(f => failureDetails[f.prNumber]?.status !== 'excluded')
                  .map((failure: MQFailureCore) => {
                    const detail = failureDetails[failure.prNumber];
                    const resolved = detail?.status === 'ready' ? detail.data : undefined;
                    return (
                      <div key={failure.prNumber} className="overview-card failure-card">
                        <div className="overview-card-header">
                          {failure.chgTicket && (
                            <a
                              href={`https://teladochealth.atlassian.net/browse/${failure.chgTicket}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="overview-card-chg"
                            >
                              {failure.chgTicket}
                            </a>
                          )}
                          <a href={failure.prUrl} target="_blank" rel="noopener noreferrer" className="overview-card-title">
                            {failure.prTitle}
                          </a>
                          <span className="overview-card-pr-number">#{failure.prNumber}</span>
                        </div>
                        <div className="overview-card-meta">
                          {resolved ? (
                            <>
                              {failureReasonContent(resolved.pipelineStages, resolved.parentBuildUrl)}
                              {failureStageContent(resolved.failureStages)}
                            </>
                          ) : detail?.status === 'error' ? (
                            <span className="overview-card-badge-error">Couldn't load failure details</span>
                          ) : (
                            <>
                              <span className="overview-card-badge-skeleton" />
                              <span className="overview-card-badge-skeleton" />
                            </>
                          )}
                          <span className="overview-card-time">{formatDate(resolved?.failedAt ?? failure.failedAt)}</span>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>

        </div>
      )}

      {pipelinePr && (
        <PipelineModal
          prNumber={pipelinePr.number}
          prTitle={pipelinePr.title}
          onClose={() => setPipelinePr(null)}
        />
      )}
    </div>
  );
};
